import { useState, useMemo, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { Select } from 'antd';

import { PropertyItem, PropertyType } from '../../../../services/realtime-db/users/users.types';
import { PropertyNames } from '../../../../common/constants';
import { TModals } from '../../accounts.page';
import { PaymentDetailsType } from 'scb-shared';

interface PropertyItemForForm extends PropertyItem {
  id: string;
}
const FormInput = ({
  properties,
  onSubmit,
  mode,
  status,
  onAccountRemove,
}: {
  status: 'loading' | 'success' | '';
  properties: PropertyItem[];
  onSubmit: (values: PropertyItem[]) => void;
  onAccountRemove?: () => void;
  mode: TModals;
}) => {
  const initialProperties = useMemo(() => {
    return properties.map((p) => ({ ...p, id: uuid() }));
  }, [properties]);

  /**
   * States
   */
  const [formItems, setFormItems] = useState<PropertyItemForForm[]>(initialProperties);
  const [errorTextAlert, setErrorTextAlert] = useState('');
  const [confirmBeforeDelete, setConfirmBeforeDelete] = useState(false);

  const onChangeProperty = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const { name, value } = e.target;

    const newFormItems = [...formItems];
    newFormItems[idx].value = value;
    setFormItems(newFormItems);
  };
  const onChangeNote = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const { name, value } = e.target;

    const newFormItems = [...formItems];
    newFormItems[idx].note = value;
    setFormItems(newFormItems);
  };

  const onRemove = useCallback(
    (id: string) => {
      const newFormItems = formItems.filter((f) => f.id !== id);
      setFormItems(newFormItems);
    },
    [formItems],
  );

  const onAdd = useCallback(() => {
    const newItem: PropertyItemForForm = {
      id: uuid(),
      value: '',
      note: '',
      type: 'none',
    };
    const newFormItems = [...formItems, newItem];
    setFormItems(newFormItems);
  }, [formItems]);

  const onSubmitHandler = useCallback(() => {
    setErrorTextAlert('');
    const someFieldsEmpty = formItems.some((f) => !f.value.trim());

    if (someFieldsEmpty) {
      setErrorTextAlert('Заповніть всі поля з реквізитами або видаліть пусті');
      return;
    }

    const itemsWithoutId = [...formItems].map((f) => {
      const { id, ...rest } = f;
      return rest;
    });

    onSubmit(itemsWithoutId);
  }, [formItems, onSubmit]);

  const submitBtn = useMemo(() => {
    if (status == 'loading' && !confirmBeforeDelete) {
      return (
        <div
          className={
            'border-2 border-primary-main bg-primary-main text-white text-[20px] w-[240px] py-[9px] rounded-md flex justify-center items-center h-[52px]'
          }
        >
          <div className="dot-flashing"></div>
        </div>
      );
    }
    const text = status === 'success' && !confirmBeforeDelete ? 'Готово' : 'Зберегти';
    return (
      <button
        onClick={onSubmitHandler}
        className={
          'border-2 border-primary-main bg-primary-main text-white text-[20px] w-[240px] py-[9px] rounded-md'
        }
      >
        {text}
      </button>
    );
  }, [confirmBeforeDelete, status, formItems, onSubmit]);

  const onChangeType = (value: PropertyType, idx: number) => {
    const newFormItems = [...formItems];
    newFormItems[idx].type = value;
    setFormItems(newFormItems);
  };

  return (
    <div>
      {!formItems.length && <div className={'text-xl'}>У вас поки немає доданих реквізитів</div>}
      {formItems.map((f, idx) => {
        return (
          <div key={f.id}>
            <div className="flex w-full items-center mt-[36px] mb-[10px]">
              <h3 className={'text-[26px] font-bold w-3/4 sm:w-1/2'}>
                <Select
                  size={'large'}
                  onChange={(val) => onChangeType(val as PropertyType, idx)}
                  className={'text-[14px] sm:text-[26px] font-bold w-full'}
                  defaultValue={PropertyNames[f.type] || PropertyNames[PaymentDetailsType.NONE]}
                >
                  {Object.entries(PropertyNames).map(([k, v]) => {
                    return <Select.Option value={k}>{v}</Select.Option>;
                  })}
                </Select>
              </h3>
              <div className={'w-1/4 flex justify-center'}>
                <button
                  onClick={() => onRemove(f.id)}
                  className={'bg-red w-[40px] h-[40px] rounded-lg flex items-center justify-center'}
                >
                  <img src="/icon-trash.svg" alt="" />
                </button>
              </div>
            </div>{' '}
            <div className="flex w-full">
              <div className="flex flex-col w-full sm:w-1/2">
                <input
                  type="text"
                  value={f.value}
                  placeholder={'Введіть реквізит'}
                  required
                  onChange={(e) => onChangeProperty(e, idx)}
                  className={
                    'border-b border-gray-200 text-[14px] sm:text-[22px] py-2 focus:outline-none'
                  }
                />
                <input
                  type="text"
                  value={f.note}
                  placeholder={'Без нотатки'}
                  onChange={(e) => onChangeNote(e, idx)}
                  className={
                    'border-b border-gray-200 text-[14px] sm:text-[22px] py-2 focus:outline-none'
                  }
                />
              </div>
            </div>
          </div>
        );
      })}
      <div className={'mt-[36px]'}>
        <button
          onClick={onAdd}
          className={
            'flex border-2 border-primary-main text-gray-600 text-[20px] w-[240px] py-[9px] rounded-md justify-center items-center'
          }
        >
          <div className={'text-[20px] text-gray-600 mr-4'}>Додати реквізит</div>
          <img src="/icon-plus.svg" alt="" />
        </button>
      </div>
      <div className={'mt-[36px]'}>{submitBtn}</div>

      {errorTextAlert && (
        <div
          className={
            'mt-[36px] border-2 border-red/25 bg-red/20 text-black text-[16px] w-3/4 text-center py-[9px] px-[9px] rounded-md'
          }
        >
          {errorTextAlert}
        </div>
      )}

      {!confirmBeforeDelete && mode === 'edit' && (
        <div className={'mt-[36px]'}>
          <button
            onClick={() => {
              setConfirmBeforeDelete(true);
            }}
            className={
              'border-2 border-red bg-red text-white text-[20px] w-[240px] py-[9px] rounded-md'
            }
          >
            Видалити рахунок
          </button>
        </div>
      )}
      {confirmBeforeDelete && mode === 'edit' && (
        <div className={'mt-[36px]'}>
          <h3 className={'text-xl mb-3'}>Ви впевнені?</h3>
          <div className={'flex'}>
            <button
              onClick={() => {
                onAccountRemove && onAccountRemove();
              }}
              className={
                'border-2 border-red bg-red text-white text-[20px] w-[240px] py-[9px] rounded-md flex justify-center items-center h-[52px]'
              }
            >
              {status === 'loading' && <div className="dot-flashing"></div>}
              {status === 'success' && 'Готово'}
              {status === '' && 'Так'}
            </button>
            <button
              onClick={() => {
                setConfirmBeforeDelete(false);
              }}
              className={
                'ml-4 border-2 border-primary-main bg-primary-main text-white text-[20px] w-[240px] py-[9px] rounded-md'
              }
            >
              Ні
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
export default FormInput;
